import { useStore } from '../../store/useStore';
import { useEffect, useRef } from 'react';
import { isMobile } from '../../utils/isMobile';

export const Minimap = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const room = useStore((state) => state.room);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !room) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animId: number | null = null;

        const drawOnce = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Background
            ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = '#333';
            ctx.strokeRect(0, 0, canvas.width, canvas.height);

            // Scale: World is roughly -60 to 60. Map is 200x200.
            // Center (0,0) is at (100, 100).
            const mapSize = 200;
            const worldSize = 120; // -60 to 60
            const scale = mapSize / worldSize;
            const offset = mapSize / 2;

            const toMap = (val: number) => val * scale + offset;

            // Draw Enemies
            room.state.enemies.forEach((enemy: any) => {
                if (enemy && typeof enemy.x === 'number') {
                    ctx.fillStyle = 'red';
                    ctx.beginPath();
                    ctx.arc(toMap(enemy.x), toMap(enemy.z), 3, 0, Math.PI * 2);
                    ctx.fill();
                }
            });

            // Draw Players
            room.state.players.forEach((player: any, sessionId: string) => {
                if (player && typeof player.x === 'number') {
                    ctx.fillStyle = sessionId === room.sessionId ? '#00ff00' : '#0000ff';
                    ctx.beginPath();
                    ctx.arc(toMap(player.x), toMap(player.z), 4, 0, Math.PI * 2);
                    ctx.fill();
                }
            });

            // Draw Items
            room.state.items.forEach((item: any) => {
                if (item) {
                    ctx.fillStyle = 'gold';
                    ctx.fillRect(toMap(item.x) - 1.5, toMap(item.z) - 1.5, 3, 3);
                }
            });
        };

        const renderLoop = () => {
            // pause when document hidden or canvas not visible
            if (document.hidden || !canvas || canvas.offsetParent === null) {
                animId = null;
                return;
            }
            drawOnce();
            animId = requestAnimationFrame(renderLoop);
        };

        const start = () => {
            if (animId !== null) return;
            if (document.hidden || !canvas || canvas.offsetParent === null) return;
            animId = requestAnimationFrame(renderLoop);
        };

        const stop = () => {
            if (animId) cancelAnimationFrame(animId);
            animId = null;
        };

        start();

        const onVisibility = () => {
            if (document.hidden) stop(); else start();
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [room]);

    if (!room) return null;

    const mobile = isMobile();

    const size = mobile ? 110 : 200;

    return (
        <div style={{
            position: 'absolute',
            bottom: mobile ? '60px' : '80px',
            right: mobile ? '18px' : '40px',
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            overflow: 'hidden',
            border: '4px solid rgba(0,0,0,0.5)',
            boxShadow: '0 0 20px rgba(0,0,0,0.5)',
            pointerEvents: 'none',
            zIndex: 900,
            background: 'rgba(0,0,0,0.8)'
        }}>
            <canvas ref={canvasRef} width={size} height={size} />
            {/* North Marker */}
            <div style={{
                position: 'absolute',
                top: '5px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderBottom: '10px solid white'
            }} />
        </div>
    );
};
